import * as Sentry from "@sentry/react";

const RETRY_MS = 400;
const MAX_RETRIES = 150;

type SessionRewindWindow = Window & {
  sessionRewind?: {
    getSessionUrl?:
      | ((cb: (sessionUrl: string) => void) => void)
      | (() => string | null | undefined);
  };
};

/** URL mới nhất từ callback hoặc get đồng bộ — dùng khi enrich event Sentry. */
let lastSessionRewindUrl: string | undefined;

let sentryRewindProcessorRegistered = false;

function setSentrySessionRewindUrl(sessionUrl: string): void {
  const trimmed = sessionUrl?.trim();
  if (!trimmed) {
    return;
  }
  lastSessionRewindUrl = trimmed;
  console.log("attachSessionRewindToSentry setSentrySessionRewindUrl", trimmed);
  const scope = Sentry.getGlobalScope?.() ?? Sentry.getCurrentScope();
  scope.setExtra("sessionUrl", trimmed);
}

/**
 * Đọc URL tại thời điểm gọi: ưu tiên cache, sau đó getSessionUrl() không tham số nếu SDK cho phép.
 * Dạng callback-only không thể trả URL đồng bộ nên phụ thuộc lastSessionRewindUrl (callback + retry).
 */
function readSessionRewindUrlNow(): string | undefined {
  if (lastSessionRewindUrl) {
    return lastSessionRewindUrl;
  }
  const getSessionUrl = (window as SessionRewindWindow).sessionRewind?.getSessionUrl;
  if (typeof getSessionUrl !== "function" || getSessionUrl.length > 0) {
    return undefined;
  }
  try {
    const syncUrl = (getSessionUrl as () => string | null | undefined)();
    const s = syncUrl != null ? String(syncUrl).trim() : "";
    if (s) {
      setSentrySessionRewindUrl(s);
      return lastSessionRewindUrl;
    }
  } catch {
    // ignore
  }
  return undefined;
}

function registerSentrySessionRewindEventProcessor(): void {
  if (sentryRewindProcessorRegistered) {
    return;
  }
  sentryRewindProcessorRegistered = true;
  Sentry.addEventProcessor((event) => {
    const url = readSessionRewindUrlNow();
    if (url) {
      event.extra = { ...event.extra, sessionUrl: url };
    }
    return event;
  });
}

/**
 * Gắn URL replay Session Rewind vào mọi event Sentry (extra "sessionUrl"),
 * theo hướng dẫn Integrations → Sentry trên Session Rewind dashboard.
 *
 * - Đăng ký event processor: mỗi lần gửi event Sentry sẽ thử lại URL (kịp nếu SDK vừa sẵn sàng).
 * - Nhánh getSessionUrl(callback): tiếp tục retry (trước đây return sớm nên không bao giờ thử lại).
 */
export function attachSessionRewindToSentry(): void {
  registerSentrySessionRewindEventProcessor();

  let retries = 0;

  const schedule = () => {
    if (retries >= MAX_RETRIES || lastSessionRewindUrl) {
      return;
    }
    retries += 1;
    setTimeout(tryBind, RETRY_MS);
  };

  function tryBind(): void {
    if (lastSessionRewindUrl) {
      return;
    }

    const sr = (window as SessionRewindWindow).sessionRewind;
    const getSessionUrl = sr?.getSessionUrl;
    if (typeof getSessionUrl !== "function") {
      schedule();
      return;
    }

    if (getSessionUrl.length > 0) {
      try {
        (getSessionUrl as (cb: (sessionUrl: string) => void) => void)((sessionUrl: string) => {
          if (sessionUrl && typeof sessionUrl === "string") {
            setSentrySessionRewindUrl(sessionUrl);
          }
        });
      } catch {
        schedule();
        return;
      }
      schedule();
      return;
    }

    try {
      const syncUrl = (getSessionUrl as () => string | null | undefined)();
      if (syncUrl) {
        setSentrySessionRewindUrl(String(syncUrl));
      } else {
        schedule();
      }
    } catch {
      schedule();
    }
  }

  tryBind();
}
